import React, { useEffect, useState } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import CustomButton from './CustomButton';
import { invoiceNotePrefill } from '../utils/invoiceNotePrefill';

export interface InvoiceOptions {
    dueDate?: Date;
    note?: string;
}

interface Props {
    open: boolean;
    /** Default payment term in days used to pre-fill the due date. */
    paymentTermDays?: number;
    /** Show the due-date field — only when an amount is still owed. */
    showDueDate: boolean;
    /** Per-tenant default note, pre-filled into the note field on a fully
     * paid invoice. A `{date}` token is replaced with today's date. */
    defaultNote?: string;
    onConfirm: (options: InvoiceOptions) => void;
    onClose: () => void;
}

// Neutral field styling: black/grey borders only. The tenant primary accent is
// reserved for buttons/CTAs and must never colour input borders (admin UI
// colour rules).
const StyledInput = styled(TextField)(({ disabled }) => ({
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: disabled ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.23)',
        },
        '&:hover fieldset': {
            borderColor: disabled ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.87)',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.87)',
            borderWidth: 1,
        },
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: 'rgba(0, 0, 0, 0.87)',
    },
    '& .MuiIconButton-root': {
        color: 'rgba(0, 0, 0, 0.54)',
    },
}));

const InvoiceOptionsDialog: React.FC<Props> = ({
    open,
    paymentTermDays,
    showDueDate,
    defaultNote,
    onConfirm,
    onClose,
}) => {
    const { t, i18n } = useTranslation();
    const language = (i18n.language || 'en').toLowerCase();
    const dateFormat = language.startsWith('de')
        ? 'DD.MM.YYYY' : 'MM/DD/YYYY';

    const [dueDate, setDueDate] = useState<Dayjs | null>(null);
    const [note, setNote] = useState('');

    // (Re)initialise each time the dialog opens.
    useEffect(() => {
        if (!open) return;
        // Pre-fill the due date only when the tenant configured a payment
        // term; otherwise leave it empty so the user picks a date actively.
        setDueDate(
            typeof paymentTermDays === 'number'
                ? dayjs().add(paymentTermDays, 'day')
                : null,
        );
        // Pre-fill the default note only on a fully paid invoice (the typical
        // prepaid statement); leave it empty when an amount is still owed.
        setNote(invoiceNotePrefill(defaultNote, !showDueDate, language));
    }, [open, paymentTermDays, showDueDate, defaultNote, language]);

    if (!open) return null;

    const dueDateMissing = showDueDate && !dueDate;

    const handleConfirm = () => {
        if (dueDateMissing) return;
        onConfirm({
            dueDate: showDueDate && dueDate ? dueDate.toDate() : undefined,
            note: note.trim() ? note.trim() : undefined,
        });
    };

    return (
        <div
            className={
                'fixed inset-0 z-[1000] flex items-center justify-center p-4'
            }
        >
            <div
                className={'fixed inset-0 bg-black bg-opacity-50 transition-opacity'}
                onClick={onClose}
            ></div>

            <div
                className={
                    'relative z-10 bg-white rounded-lg shadow-xl w-full ' +
                    'max-w-md mx-auto'
                }
                role={'dialog'}
                aria-modal={'true'}
                aria-labelledby={'invoice-options-title'}
            >
                <div className={'p-6'}>
                    <h3
                        className={'text-lg font-bold text-gray-900 mb-1'}
                        id={'invoice-options-title'}
                    >
                        {t('invoice.options_dialog.title')}
                    </h3>
                    <p className={'text-sm text-gray-500 mb-4'}>
                        {t('invoice.options_dialog.description')}
                    </p>
                    <div className={'flex flex-col gap-4'}>
                        {showDueDate && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label={t('invoice.options_dialog.due_date')}
                                    value={dueDate}
                                    onChange={(value) => setDueDate(value)}
                                    format={dateFormat}
                                    slots={{ textField: StyledInput }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: 'outlined',
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        )}
                        <StyledInput
                            label={t('invoice.options_dialog.note')}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            fullWidth={true}
                            multiline={true}
                            minRows={3}
                            variant={'outlined'}
                            InputProps={{
                                sx: { alignItems: 'flex-start' },
                                endAdornment: note ? (
                                    <InputAdornment
                                        position={'end'}
                                        sx={{ mt: 1 }}
                                    >
                                        <IconButton
                                            aria-label={t(
                                                'invoice.options_dialog'
                                                + '.clear_note',
                                            )}
                                            onClick={() => setNote('')}
                                            edge={'end'}
                                            size={'small'}
                                        >
                                            <CloseIcon fontSize={'small'} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />
                    </div>
                </div>

                <div className={'px-6 py-4 flex flex-row-reverse gap-3'}>
                    <CustomButton
                        title={t('invoice.options_dialog.confirm')}
                        onClick={handleConfirm}
                        disabled={dueDateMissing}
                    />
                    <CustomButton
                        title={t('invoice.options_dialog.cancel')}
                        onClick={onClose}
                        isFilled={false}
                    />
                </div>
            </div>
        </div>
    );
};

export default InvoiceOptionsDialog;
